import React from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, CardActionArea } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PeopleIcon from '@mui/icons-material/People';
import SeedIcon from '@mui/icons-material/GrassOutlined';
import ScienceIcon from '@mui/icons-material/Science';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import ApiIcon from '@mui/icons-material/Api';
import WorkIcon from '@mui/icons-material/Work';
import StorageIcon from '@mui/icons-material/Storage';
import NfcIcon from '@mui/icons-material/Nfc';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import BuildIcon from '@mui/icons-material/Build';

const useStyles = makeStyles((theme: any) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0d1117 0%, #161b22 50%, #0d1117 100%)',
    padding: '32px',
    fontFamily: '"Inter", "Segoe UI", sans-serif',
    color: '#f0f6fc',
  },
  header: {
    textAlign: 'center',
    marginBottom: '48px',
  },
  title: {
    fontSize: '3rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '16px',
  },
  subtitle: {
    fontSize: '1.2rem',
    color: '#8b949e',
    fontWeight: 400,
  },
  sectionTitle: {
    fontSize: '2rem',
    fontWeight: 600,
    color: '#f0f6fc',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sectionIcon: {
    padding: '8px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
  },
  card: {
    background: 'rgba(22, 27, 34, 0.8)',
    border: '1px solid #30363d',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#22c55e',
      boxShadow: '0 8px 32px rgba(34, 197, 94, 0.2)',
      transform: 'translateY(-4px)',
    },
  },
  cardContent: {
    padding: '24px',
    textAlign: 'center',
  },
  cardIcon: {
    fontSize: '3rem',
    color: '#22c55e',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#f0f6fc',
    marginBottom: '8px',
  },
  cardDescription: {
    fontSize: '0.9rem',
    color: '#8b949e',
    lineHeight: 1.5,
  },
}));

const MachineManagementLandingPage: React.FunctionComponent = () => {
  const classes = useStyles();
  const history = useHistory();

  // AutoTreat navigation options
  const autoTreatOptions = [
    {
      title: 'Customers',
      icon: PeopleIcon,
      description: 'Manage customer accounts and information',
      path: '/autotreat/customers',
    },
    { title: 'Seeds', icon: SeedIcon, description: 'Track seed inventory and varieties', path: '/autotreat/seeds' },
    {
      title: 'Treatment Products',
      icon: ScienceIcon,
      description: 'Manage treatment product catalog',
      path: '/autotreat/treatment-products',
    },
    {
      title: 'Recipes',
      icon: MenuBookIcon,
      description: 'Create and manage treatment recipes',
      path: '/autotreat/recipes',
    },
    {
      title: 'Orders',
      icon: ShoppingCartIcon,
      description: 'Process and track treatment orders',
      path: '/autotreat/orders',
    },
    {
      title: 'Seed Sources',
      icon: StorageIcon,
      description: 'Manage seed source locations',
      path: '/autotreat/seed-sources',
    },
    {
      title: 'Treatment Sources',
      icon: LocalShippingIcon,
      description: 'Track treatment material sources',
      path: '/autotreat/treatment-sources',
    },
    {
      title: 'Transaction API Log',
      icon: ApiIcon,
      description: 'View API transaction logs',
      path: '/autotreat/transaction-log',
    },
  ];

  // AutoBulk navigation options
  const autoBulkOptions = [
    {
      title: 'Customers',
      icon: PeopleIcon,
      description: 'Manage customer accounts and information',
      path: '/autobulk/customers',
    },
    {
      title: 'Products',
      icon: InventoryIcon,
      description: 'Manage product catalog and inventory',
      path: '/autobulk/products',
    },
    {
      title: 'Work Orders',
      icon: WorkIcon,
      description: 'Create and track work orders',
      path: '/autobulk/work-orders',
    },
    { title: 'Sources', icon: StorageIcon, description: 'Manage material source locations', path: '/autobulk/sources' },
    {
      title: 'Transports',
      icon: LocalShippingIcon,
      description: 'Track transport and logistics',
      path: '/autobulk/transports',
    },
    { title: 'RFIDs', icon: NfcIcon, description: 'Manage RFID tags and tracking', path: '/autobulk/rfids' },
    {
      title: 'Drivers',
      icon: DriveEtaIcon,
      description: 'Manage driver information and schedules',
      path: '/autobulk/drivers',
    },
    {
      title: 'Transaction API Log',
      icon: ApiIcon,
      description: 'View API transaction logs',
      path: '/autobulk/transaction-log',
    },
  ];

  const handleCardClick = (path: string) => {
    history.push(path);
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.title}>Machine Management</Typography>
        <Typography className={classes.subtitle}>Access and manage your AutoTreat and AutoBulk systems</Typography>
      </Box>

      {/* AutoTreat Section */}
      <Box mb={6}>
        <Box className={classes.sectionTitle}>
          <Box className={classes.sectionIcon}>
            <ScienceIcon style={{ color: '#fff', fontSize: '2rem' }} />
          </Box>
          AutoTreat Management
        </Box>
        <Grid container spacing={3}>
          {autoTreatOptions.map((option, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Box className={classes.card} onClick={() => handleCardClick(option.path)}>
                <Box className={classes.cardContent}>
                  <option.icon className={classes.cardIcon} />
                  <Typography className={classes.cardTitle}>{option.title}</Typography>
                  <Typography className={classes.cardDescription}>{option.description}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* AutoBulk Section */}
      <Box>
        <Box className={classes.sectionTitle}>
          <Box className={classes.sectionIcon}>
            <LocalShippingIcon style={{ color: '#fff', fontSize: '2rem' }} />
          </Box>
          AutoBulk Management
        </Box>
        <Grid container spacing={3}>
          {autoBulkOptions.map((option, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Box className={classes.card} onClick={() => handleCardClick(option.path)}>
                <Box className={classes.cardContent}>
                  <option.icon className={classes.cardIcon} />
                  <Typography className={classes.cardTitle}>{option.title}</Typography>
                  <Typography className={classes.cardDescription}>{option.description}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default MachineManagementLandingPage;
